import { useCallback, useMemo, useState, type ReactNode } from 'react'

import { useResolvedTimezone } from '@/hooks/useResolvedTimezone'
import {
  ActiveTimezoneContext,
  RouteZoneContext,
  TimezoneHostContext,
  ZoneContext,
  type ActiveTimezoneScope,
  type RouteZoneKeys,
  type TimezoneHostApi,
} from '@/hooks/useZone'

import { routeZone } from './timezoneMap'

/**
 * The app's time zone, per page — mounted once by `AppShell`, around the
 * header and the page.
 *
 * It works out which sidebar module and page the URL is, and gives every page
 * that page's zone (`useZone()`), so a page needs nothing of its own to follow
 * the person's choice for it. A page that shows a store adds a
 * `TimezoneScope`, which registers here so the header can offer that store's
 * rows; the deepest scope on screen wins, and among equals the latest to
 * arrive — a page swapped for another mounts its scope before the old one
 * leaves.
 */
export function TimezoneHost({ pathname, children }: { pathname: string; children: ReactNode }) {
  const [scopes, setScopes] = useState<ReadonlyMap<symbol, ActiveTimezoneScope>>(new Map())

  const enter = useCallback((id: symbol, scope: ActiveTimezoneScope) => {
    setScopes((current) => new Map(current).set(id, scope))
  }, [])
  const leave = useCallback((id: symbol) => {
    setScopes((current) => {
      if (!current.has(id)) return current
      const next = new Map(current)
      next.delete(id)
      return next
    })
  }, [])
  const api = useMemo<TimezoneHostApi>(() => ({ enter, leave }), [enter, leave])

  const active = useMemo(() => {
    let deepest: ActiveTimezoneScope | null = null
    for (const scope of scopes.values()) {
      if (!deepest || scope.depth >= deepest.depth) deepest = scope
    }
    return deepest
  }, [scopes])

  const { module, page, moduleLabel, pageLabel } = routeZone(pathname)
  const route = useMemo<RouteZoneKeys>(
    () => ({
      module,
      page,
      ...(moduleLabel ? { moduleLabel } : {}),
      ...(pageLabel ? { pageLabel } : {}),
    }),
    [module, page, moduleLabel, pageLabel],
  )
  const zone = useResolvedTimezone({ page }).zone

  return (
    <TimezoneHostContext value={api}>
      <RouteZoneContext value={route}>
        <ActiveTimezoneContext value={active}>
          <ZoneContext value={zone}>{children}</ZoneContext>
        </ActiveTimezoneContext>
      </RouteZoneContext>
    </TimezoneHostContext>
  )
}
